import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Upload, FileText, CheckCircle2, AlertCircle, Loader2, ShieldCheck,
  TrendingUp, ArrowRight, Lock, Zap, Target, Sparkles,
} from 'lucide-react';
import './FundingQualifierPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps action plan recommendedService → internal route for the CTA card
const SERVICE_ROUTE = {
  'Credit Repair': '/credit-repair',
  'Primary Tradelines': '/primary-tradelines',
  'Wealth Rebuild Bundle': '/wealth-rebuild',
  'Student Loan Removal': '/credit-repair',
  '90-Day Mentorship': '/mentorship',
  'Business Formation': '/business-formation',
  'Shelf Corporation': '/shelf-corporations',
  'Inquiry Removal': '/services',
  'ChexSystems Removal': '/services',
  '7-Day Credit Sweep': '/services',
  'Capital Funding Service': '/services',
};

const TIER_COLOR = {
  'Prime': { color: '#4ade80', bg: 'rgba(74, 222, 128, 0.12)' },
  'Near-Prime': { color: '#ffd76a', bg: 'rgba(255, 215, 106, 0.12)' },
  'Subprime': { color: '#fb923c', bg: 'rgba(251, 146, 60, 0.12)' },
  'Deep Subprime': { color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)' },
};

const initialLead = { firstName: '', lastName: '', email: '', phone: '' };

const FundingQualifierPage = () => {
  const [step, setStep] = useState('lead'); // lead | upload | analyzing | results | error
  const [lead, setLead] = useState(initialLead);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const onLeadChange = (e) => setLead({ ...lead, [e.target.name]: e.target.value });

  const submitLead = (e) => {
    e.preventDefault();
    if (!lead.firstName.trim() || !lead.email.includes('@')) {
      setError('Please enter your first name and a valid email.');
      return;
    }
    setError('');
    setStep('upload');
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported.');
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      setError('File too large (max 15 MB).');
      return;
    }
    setError('');
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files[0]);
  };

  const submitAnalysis = async () => {
    if (!file) {
      setError('Please upload your tri-merge credit report PDF.');
      return;
    }
    setError('');
    setStep('analyzing');

    const form = new FormData();
    form.append('firstName', lead.firstName);
    form.append('lastName', lead.lastName);
    form.append('email', lead.email);
    form.append('phone', lead.phone);
    form.append('pdf', file);

    try {
      const res = await fetch(`${API}/funding-qualifier/analyze`, {
        method: 'POST',
        body: form,
      });
      let data = null;
      try { data = await res.json(); } catch (_) { data = null; }
      if (!res.ok || !data?.success) {
        const msg = data?.detail
          || (res.status === 413 ? 'Your file is too large for the server to accept. Try a smaller PDF (under 15 MB).'
            : res.status === 504 || res.status === 502 ? 'The analysis engine timed out. Please try again — it usually works on the second try.'
            : `Analysis failed (HTTP ${res.status}). Please try again.`);
        throw new Error(msg);
      }
      setResult(data);
      setStep('results');
    } catch (err) {
      console.error('Funding qualifier error:', err);
      const msg = err?.name === 'TypeError'
        ? 'Could not reach the server. Check your internet connection and try again.'
        : (err?.message || 'Something went wrong.');
      setError(msg);
      setStep('error');
    }
  };

  return (
    <div className="dark-page fq-page">
      {/* Hero */}
      <section className="fq-hero">
        <div className="fq-hero-inner">
          <div className="fq-badge" data-testid="fq-badge">
            <Sparkles size={14} /> FUNDING READINESS QUALIFIER
          </div>
          <h1 className="display-huge">
            Find Out <span className="text-gradient">Exactly How Much Funding</span> You Qualify For
          </h1>
          <p className="body-large fq-subtitle">
            Upload your tri-merge credit report and our team's proprietary engine (built on Collins Academy's playbook)
            tells you your funding tier, estimated approval ranges, and a prioritized action plan — in under 60 seconds.
          </p>
          <div className="fq-trust-row">
            <div className="fq-trust-item"><Lock size={18} /><span>Bank-grade encryption</span></div>
            <div className="fq-trust-item"><ShieldCheck size={18} /><span>Never shared or sold</span></div>
            <div className="fq-trust-item"><Zap size={18} /><span>Results in ~60 seconds</span></div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="fq-body">
        <div className="fq-card" data-testid="fq-main-card">
          {/* Progress steps */}
          <div className="fq-steps">
            <StepDot active={step === 'lead'} done={step !== 'lead'} label="Your Info" />
            <div className="fq-step-line" />
            <StepDot active={step === 'upload'} done={['analyzing', 'results'].includes(step)} label="Upload Report" />
            <div className="fq-step-line" />
            <StepDot active={['analyzing', 'results'].includes(step)} done={step === 'results'} label="Your Results" />
          </div>

          {step === 'lead' && (
            <form className="fq-form" onSubmit={submitLead} data-testid="fq-lead-form">
              <h2 className="fq-section-title">Step 1 — Tell us where to send your report</h2>
              <p className="fq-section-sub">We'll email your full analysis the moment it's ready.</p>
              <div className="fq-grid-2">
                <Field label="First Name *" name="firstName" value={lead.firstName} onChange={onLeadChange} testid="fq-firstname" required />
                <Field label="Last Name" name="lastName" value={lead.lastName} onChange={onLeadChange} testid="fq-lastname" />
                <Field label="Email *" name="email" type="email" value={lead.email} onChange={onLeadChange} testid="fq-email" required />
                <Field label="Phone" name="phone" value={lead.phone} onChange={onLeadChange} testid="fq-phone" />
              </div>
              {error && <div className="fq-error" data-testid="fq-error"><AlertCircle size={16} /> {error}</div>}
              <button type="submit" className="btn-primary fq-next-btn" data-testid="fq-continue-btn">
                Continue <ArrowRight size={18} />
              </button>
            </form>
          )}

          {step === 'upload' && (
            <div className="fq-upload-wrap" data-testid="fq-upload-step">
              <h2 className="fq-section-title">Step 2 — Upload your tri-merge credit report</h2>
              <p className="fq-section-sub">
                Supported: Experian, Equifax, TransUnion, SmartCredit, IdentityIQ, MyScoreIQ.
                PDF only, up to 15 MB.
              </p>

              <div
                className={`fq-dropzone ${dragOver ? 'fq-dropzone-over' : ''} ${file ? 'fq-dropzone-filled' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="fq-dropzone"
                role="button"
                tabIndex={0}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => pickFile(e.target.files?.[0])}
                  data-testid="fq-file-input"
                />
                {file ? (
                  <>
                    <FileText size={40} className="fq-dropzone-icon" />
                    <div className="fq-dropzone-title">{file.name}</div>
                    <div className="fq-dropzone-sub">{(file.size / 1024 / 1024).toFixed(2)} MB — click to change</div>
                  </>
                ) : (
                  <>
                    <Upload size={40} className="fq-dropzone-icon" />
                    <div className="fq-dropzone-title">Drop your PDF here or click to browse</div>
                    <div className="fq-dropzone-sub">Your file is encrypted the moment it arrives</div>
                  </>
                )}
              </div>

              {error && <div className="fq-error" data-testid="fq-error"><AlertCircle size={16} /> {error}</div>}

              <div className="fq-upload-actions">
                <button type="button" className="fq-back-btn" onClick={() => setStep('lead')} data-testid="fq-back-btn">
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary fq-next-btn"
                  onClick={submitAnalysis}
                  disabled={!file}
                  data-testid="fq-analyze-btn"
                >
                  Analyze My Report <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {step === 'analyzing' && <AnalyzingState />}

          {step === 'results' && result && (
            <ResultsView result={result} leadName={lead.firstName} />
          )}

          {step === 'error' && (
            <div className="fq-error-state" data-testid="fq-error-state">
              <AlertCircle size={40} />
              <h2>We hit a snag</h2>
              <p>{error}</p>
              <button type="button" className="btn-primary" onClick={() => setStep('upload')}>
                Try Again
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const StepDot = ({ active, done, label }) => (
  <div className={`fq-step ${active ? 'fq-step-active' : ''} ${done ? 'fq-step-done' : ''}`}>
    <div className="fq-step-dot">{done ? <CheckCircle2 size={14} /> : null}</div>
    <div className="fq-step-label">{label}</div>
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', testid, required }) => (
  <label className="fq-field">
    <span>{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      autoComplete="off"
    />
  </label>
);

const AnalyzingState = () => (
  <div className="fq-analyzing" data-testid="fq-analyzing">
    <Loader2 size={48} className="fq-spin" />
    <h2 className="fq-analyzing-title">Analyzing your credit report…</h2>
    <ul className="fq-analyzing-list">
      <li><CheckCircle2 size={16} /> Encrypting your file</li>
      <li><CheckCircle2 size={16} /> Extracting credit metrics</li>
      <li><Loader2 size={16} className="fq-spin" /> Running funding qualification analysis</li>
      <li className="fq-pending">Building your personalized action plan</li>
    </ul>
    <p className="fq-analyzing-sub">This usually takes 30–60 seconds. Please don't close this window.</p>
  </div>
);

const FuelGauge = ({ tier, readinessScore }) => {
  // 5-segment fuel gauge: High Risk → Distressed → Building → Strong → Elite.
  // Position is derived from tier first (most reliable), then refined by readinessScore.
  const TIER_TO_INDEX = {
    'Deep Subprime': 0,
    'Subprime': 1,
    'Near-Prime': 2,
    'Prime': 3,
  };
  const segments = [
    { label: 'High Risk',  color: '#f87171', range: '<580'    },
    { label: 'Distressed', color: '#fb923c', range: '580-639' },
    { label: 'Building',   color: '#fbbf24', range: '640-699' },
    { label: 'Strong',     color: '#a3e635', range: '700-749' },
    { label: 'Elite',      color: '#4ade80', range: '750+'    },
  ];
  let activeIndex = TIER_TO_INDEX[tier] ?? 2;
  // If tier=Prime and readiness >= 85, promote to Elite.
  if (tier === 'Prime' && (readinessScore ?? 0) >= 85) activeIndex = 4;
  if (typeof readinessScore === 'number' && tier === undefined) {
    if (readinessScore >= 85) activeIndex = 4;
    else if (readinessScore >= 70) activeIndex = 3;
    else if (readinessScore >= 50) activeIndex = 2;
    else if (readinessScore >= 30) activeIndex = 1;
    else activeIndex = 0;
  }
  // Map active segment to needle angle (-90deg = High Risk, +90deg = Elite).
  const angle = -90 + (activeIndex * 45);
  const active = segments[activeIndex];

  return (
    <div className="fq-gauge" data-testid="fq-fuel-gauge">
      <div className="fq-gauge-title">Funding Readiness</div>
      <div className="fq-gauge-arc">
        <svg viewBox="0 0 200 110" className="fq-gauge-svg" aria-hidden="true">
          <defs>
            <linearGradient id="fq-gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="#f87171" />
              <stop offset="25%"  stopColor="#fb923c" />
              <stop offset="50%"  stopColor="#fbbf24" />
              <stop offset="75%"  stopColor="#a3e635" />
              <stop offset="100%" stopColor="#4ade80" />
            </linearGradient>
          </defs>
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            stroke="url(#fq-gauge-grad)"
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            opacity="0.35"
          />
          {/* Active arc highlight */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            stroke="url(#fq-gauge-grad)"
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            strokeDasharray="251.3"
            strokeDashoffset={251.3 - (activeIndex / 4) * 251.3 - 1}
          />
          {/* Tick marks */}
          {[-90, -45, 0, 45, 90].map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            const x1 = 100 + Math.cos(rad - Math.PI / 2) * 70;
            const y1 = 100 + Math.sin(rad - Math.PI / 2) * 70;
            const x2 = 100 + Math.cos(rad - Math.PI / 2) * 80;
            const y2 = 100 + Math.sin(rad - Math.PI / 2) * 80;
            return (
              <line
                key={i}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={segments[i].color}
                strokeWidth="2"
                opacity={i === activeIndex ? 1 : 0.4}
              />
            );
          })}
          {/* Needle */}
          <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: '100px 100px', transition: 'transform 900ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <line x1="100" y1="100" x2="100" y2="32" stroke="#ffd76a" strokeWidth="3" strokeLinecap="round" />
            <circle cx="100" cy="32" r="4" fill="#ffd76a" />
          </g>
          <circle cx="100" cy="100" r="9" fill="#1a0d3d" stroke="#ffd76a" strokeWidth="3" />
        </svg>
      </div>
      <div className="fq-gauge-readout" style={{ color: active.color }}>
        {active.label}
      </div>
      <div className="fq-gauge-segments">
        {segments.map((s, i) => (
          <div
            key={i}
            className={`fq-gauge-seg ${i === activeIndex ? 'fq-gauge-seg-active' : ''}`}
            style={i === activeIndex ? { borderColor: s.color, color: s.color } : null}
          >
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const GatedHighRiskView = ({ result, leadName }) => {
  const { negativeItemCount = 0, highSeverityCount = 0, fundingEligibility: fe = {}, calendlyUrl, gateMessage } = result;
  // Dynamic stalled-amount headline. Defaults to $200K when no estimate available.
  const stalledHigh = Math.max(fe.estimatedPersonalFundingHigh || 0, fe.estimatedBusinessFundingHigh || 0);
  const stalledAmount = stalledHigh >= 25000
    ? `$${Math.round(stalledHigh / 1000)}K`
    : '$200K';
  return (
    <div className="fq-gated" data-testid="fq-gated-view">
      <div className="fq-gated-alert">
        <AlertCircle size={28} className="fq-gated-icon" />
        <div className="fq-gated-headline">High-Risk Profile Detected</div>
      </div>
      <h2 className="fq-gated-title" data-testid="fq-gated-headline">
        Your {stalledAmount} Funding is Stalled: <span className="fq-gated-title-accent">Schedule your Strategy Session.</span>
      </h2>
      <p className="fq-gated-summary" data-testid="fq-gated-summary">
        {leadName ? `${leadName}, we` : 'We'} flagged <strong>{negativeItemCount}</strong> negative item{negativeItemCount !== 1 ? 's' : ''} on your credit report
        {highSeverityCount > 0 ? <> — <strong>{highSeverityCount} high-severity</strong> (collections, charge-offs, or public records)</> : null}.
        These items are actively suppressing your funding eligibility and approval odds.
      </p>
      <FuelGauge tier={fe.tier} readinessScore={fe.readinessScore} />
      <div className="fq-gated-stat-row">
        <div className="fq-gated-stat">
          <div className="fq-gated-stat-num">{negativeItemCount}</div>
          <div className="fq-gated-stat-label">Negative Items Detected</div>
        </div>
        <div className="fq-gated-stat">
          <div className="fq-gated-stat-num fq-gated-stat-warn">{highSeverityCount}</div>
          <div className="fq-gated-stat-label">High-Severity Issues</div>
        </div>
        <div className="fq-gated-stat">
          <div className="fq-gated-stat-num fq-gated-stat-accent">{fe.tier || '—'}</div>
          <div className="fq-gated-stat-label">Current Funding Tier</div>
        </div>
      </div>
      <p className="fq-gated-cta-line">{gateMessage}</p>
      <a
        href={calendlyUrl || 'https://calendly.com/thecollinsacademynew/30min'}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary fq-gated-cta-btn"
        data-testid="fq-gated-book-btn"
      >
        Book My Free Consultation <ArrowRight size={20} />
      </a>
      <p className="fq-gated-note">
        Your full strategy session is complimentary. We'll walk you through every item, your dispute strategy, and exactly what it takes to unlock funding.
      </p>
    </div>
  );
};

const REPAIR_SERVICES = ['Credit Repair', 'Student Loan Removal', 'Wealth Rebuild Bundle', '7-Day Credit Sweep'];

const ResultsView = ({ result, leadName }) => {
  const navigate = useNavigate();
  const { metrics = {}, fundingEligibility: fe = {}, strengths = [], weaknesses = [], actionPlan = [] } = result;

  // Needs credit repair → auto-redirect to the Wealth Rebuild Bundle (credit repair + student loan removal)
  const needsRepair = !result.gated && (
    (metrics.derogatoryMarks ?? 0) > 0 ||
    (result.negativeItemCount ?? 0) > 0 ||
    actionPlan.some((item) => REPAIR_SERVICES.includes(item.recommendedService))
  );
  const [countdown, setCountdown] = useState(15);

  // Summary handed to /wealth-rebuild for a personalized greeting
  const scanSummary = {
    firstName: leadName || '',
    avgScore: metrics.creditScores?.average ?? null,
    derogatoryMarks: metrics.derogatoryMarks ?? result.negativeItemCount ?? 0,
    fundingHigh: Math.max(fe.estimatedPersonalFundingHigh || 0, fe.estimatedBusinessFundingHigh || 0) || null,
    tier: fe.tier || null,
  };

  useEffect(() => {
    if (!needsRepair) return;
    const iv = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(iv);
  }, [needsRepair]);

  useEffect(() => {
    if (needsRepair && countdown <= 0) {
      navigate('/wealth-rebuild', { state: { scan: scanSummary } });
    }
  }, [needsRepair, countdown, navigate, scanSummary]);

  // High-risk profiles get a fully gated view (no metrics, no action plan) until they book.
  if (result.gated) {
    return <GatedHighRiskView result={result} leadName={leadName} />;
  }
  const tierMeta = TIER_COLOR[fe.tier] || TIER_COLOR['Near-Prime'];
  const scores = metrics.creditScores || {};
  const avgScore = scores.average;

  return (
    <div className="fq-results" data-testid="fq-results">
      {needsRepair && (
        <div className="fq-redirect-banner" data-testid="fq-redirect-banner">
          <div className="fq-redirect-copy">
            <Sparkles size={18} />
            <span>
              <strong>We found your fix — the Wealth Rebuild Bundle™</strong> (credit repair + student loan
              removal + tradelines). Taking you there in <strong>{Math.max(countdown, 0)}s</strong>…
            </span>
          </div>
          <Link
            to="/wealth-rebuild"
            state={{ scan: scanSummary }}
            className="btn-primary fq-redirect-btn"
            data-testid="fq-redirect-now-btn"
          >
            Take Me There Now <ArrowRight size={16} />
          </Link>
        </div>
      )}
      <div className="fq-results-hero">
        <div className="fq-verdict-badge" style={{ background: tierMeta.bg, color: tierMeta.color, borderColor: tierMeta.color }}>
          {fe.tier || '—'}
        </div>
        <h2 className="fq-verdict-title">
          {leadName ? `${leadName}, here's your funding profile` : "Here's your funding profile"}
        </h2>
        {fe.oneLineVerdict && <p className="fq-verdict-line">{fe.oneLineVerdict}</p>}
      </div>

      <FuelGauge tier={fe.tier} readinessScore={fe.readinessScore} />

      <div className="fq-stat-grid">
        <Stat label="Average Credit Score" value={avgScore ?? '—'} accent />
        <Stat label="Readiness Score" value={fe.readinessScore != null ? `${fe.readinessScore}/100` : '—'} />
        <Stat label="Utilization" value={metrics.utilizationPct != null ? `${metrics.utilizationPct}%` : '—'} />
        <Stat label="Open Accounts" value={metrics.openAccounts ?? '—'} />
        <Stat label="Derogatory Marks" value={metrics.derogatoryMarks ?? '—'} warn={metrics.derogatoryMarks > 0} />
        <Stat label="Hard Inquiries" value={metrics.hardInquiries ?? '—'} warn={metrics.hardInquiries > 6} />
      </div>

      <div className="fq-funding-row">
        <FundingBand
          title="Personal Funding Estimate"
          low={fe.estimatedPersonalFundingLow}
          high={fe.estimatedPersonalFundingHigh}
        />
        <FundingBand
          title="Business Funding Estimate"
          low={fe.estimatedBusinessFundingLow}
          high={fe.estimatedBusinessFundingHigh}
        />
      </div>

      {(strengths.length > 0 || weaknesses.length > 0) && (
        <div className="fq-grid-2 fq-sw">
          {strengths.length > 0 && (
            <div className="fq-sw-card fq-sw-good" data-testid="fq-strengths">
              <h3><CheckCircle2 size={18} /> Strengths</h3>
              <ul>{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {weaknesses.length > 0 && (
            <div className="fq-sw-card fq-sw-bad" data-testid="fq-weaknesses">
              <h3><AlertCircle size={18} /> Weaknesses</h3>
              <ul>{weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      {actionPlan.length > 0 && (
        <div className="fq-plan" data-testid="fq-action-plan">
          <h3 className="fq-plan-title"><Target size={20} /> Your Prioritized Action Plan</h3>
          <div className="fq-plan-list">
            {actionPlan.map((item, i) => {
              const route = item.serviceSlug && item.serviceSlug.startsWith('/')
                ? item.serviceSlug
                : SERVICE_ROUTE[item.recommendedService];
              return (
                <div key={i} className="fq-plan-item">
                  <div className="fq-plan-num">{item.priority ?? i + 1}</div>
                  <div className="fq-plan-body">
                    <div className="fq-plan-item-title">{item.title}</div>
                    <div className="fq-plan-item-detail">{item.detail}</div>
                    {item.recommendedService && item.recommendedService !== 'None' && route && (
                      <Link to={route} className="fq-plan-cta" data-testid={`fq-plan-cta-${i}`}>
                        {item.recommendedService} <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="fq-cta-footer">
        <h3>Want a strategist to walk you through this?</h3>
        <p>Book a free 30-minute consultation and turn this roadmap into real approvals.</p>
        <a
          href="https://calendly.com/thecollinsacademynew/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary fq-next-btn"
          data-testid="fq-book-call"
        >
          Book My Free Consultation <ArrowRight size={18} />
        </a>
        <p className="fq-cta-note">Your full written report has been emailed to you.</p>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent, warn }) => (
  <div className={`fq-stat ${accent ? 'fq-stat-accent' : ''} ${warn ? 'fq-stat-warn' : ''}`}>
    <div className="fq-stat-value">{value}</div>
    <div className="fq-stat-label">{label}</div>
  </div>
);

const FundingBand = ({ title, low, high }) => {
  const fmt = (n) => (typeof n === 'number' ? `$${n.toLocaleString()}` : '—');
  return (
    <div className="fq-band">
      <div className="fq-band-title"><TrendingUp size={16} /> {title}</div>
      <div className="fq-band-range">{fmt(low)} <span>–</span> {fmt(high)}</div>
    </div>
  );
};

export default FundingQualifierPage;
